import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, OfflineError } from '../api/http';
import { fetchHeightmap } from '../api/missions';
import {
  deleteMission,
  fetchRecipes,
  regenerateMission,
  missionDownloadUrl,
  setMissionCover,
  fetchHistory,
  restoreRevision,
  fetchMission,
  patchMission,
  saveMission,
  type Definition,
  type Force,
  type MissionView,
  type Objective,
  type OptionalView,
  type PatchOp,
  type RecipeView,
  type RevisionView,
} from '../api/missions';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import {
  AreaPanel,
  CoverPanel,
  SetupPanel,
  BriefingPanel,
  CheckPanel,
  CiviliansPanel,
  CompositionsPanel,
  ForcesPanel,
  IntelPanel,
  MapChrome,
  MarkersPanel,
  CastPanel,
  FactionsPanel,
  RespawnPanel,
  StoryPanel,
  ViewDistance,
  TwistsPanel,
} from './DesignPanels';
import {
  ALL_LAYERS,
  checks,
  newArea,
  newCivilianZone,
  newComposition,
  newForce,
  newIntel,
  newMarker,
  newRestrictedZone,
  placesOf,
  worldSizeOf,
  type LayerState,
  type Selection,
  type Tool,
} from './designModel';
import type { Cover } from './missionCovers';
import { MissionMap } from './MissionMap';
import { PlannedButton } from './Planned';
import { useConfirm } from './useConfirm';
import { PageLoading } from './PageStates';
import { Shell } from './Shell';
import { applyOp, useDesignLive } from './useDesignLive';
import { editorColour, editorSlots, fieldOfPath, panelOfFocus, panelOfPath } from './editorColours';
import { messageOf } from './newMissionErrors';
import { mayChange, ruleFor } from './editRules';
import { ScriptPanel } from './ScriptPanel';
import { PlaceMover } from './PlaceMover';
import { defaultAlertWith, placeLabel, secondariesWith, twistsWith } from './scriptWords';

const TYPES = [
  'destroy_installation',
  'capture_hvt',
  'rescue_hostage',
  'defend_position',
  'escort_convoy',
  'clear_area',
  'retrieve_intel',
  'defuse_bomb',
  'deliver_supplies',
  'humanitarian_aid',
  'ambush_convoy',
  'ambush_reaction',
  'sabotage_vehicles',
  'exfiltrate',
  'bribe_informant',
];

type PanelKey =
  | 'setup'
  | 'objectives'
  | 'script'
  | 'area'
  | 'markers'
  | 'forces'
  | 'compositions'
  | 'civilians'
  | 'intel'
  | 'twists'
  | 'briefing'
  | 'cast'
  | 'story'
  | 'factions'
  | 'loadouts'
  | 'supports'
  | 'rules'
  | 'check';

const PANELS: PanelKey[] = [
  'setup',
  'objectives',
  // Only for a mission whose definition carries a script (ENGINE_DESIGN.md §2.20).
  'script',
  'area',
  'markers',
  'forces',
  'compositions',
  'civilians',
  'intel',
  'twists',
  'briefing',
  'cast',
  'story',
  'factions',
  'loadouts',
  'supports',
  'rules',
  'check',
];

/** Which panel a thing selected on the map belongs to. */
const PANEL_OF: Record<Selection['kind'], PanelKey> = {
  objective: 'objectives',
  marker: 'markers',
  force: 'forces',
  intel: 'intel',
  civilian: 'civilians',
  area: 'area',
  zone: 'area',
  start: 'setup',
  composition: 'compositions',
  twist: 'twists',
  // Never opened from the map (the select below keeps the panel): a place is
  // moved from the Guió panel or from the bar under the map.
  place: 'script',
};

/** The tools that drop a force, and the role each drops. */
const FORCE_TOOLS: Partial<Record<Tool, Force['role']>> = {
  patrol: 'patrol',
  garrison: 'garrison',
  guard: 'guard',
  static: 'static',
  vehicle: 'vehicle',
};

function errorText(err: unknown): string {
  if (err instanceof OfflineError) return ca.errors.offline;
  return err instanceof ApiError ? (ca.errors[err.code] ?? ca.errors.unknown) : ca.errors.unknown;
}

/**
 * The designer (UI doc 5.7). A panel rail on the left, the map in the middle
 * and the selected thing's form on the right.
 *
 * The map is the editing surface: an area of operations drawn by dragging a
 * box, NATO markers for what the players are told, patrols with their route,
 * garrisons, guard posts and static weapons for what they are not, civilian
 * zones, and intelligence pins for what the briefing says. Each of those is a
 * layer that can be turned off, a tool that places it and a panel that edits
 * it, so a mission can be built end to end from the browser. What Eden and Zeus
 * do through a right-click menu, this does with a tool and a form.
 */
export function Designer() {
  const { code = '' } = useParams();
  const navigate = useNavigate();
  const { me } = useAuth();
  const [mission, setMission] = useState<MissionView | null>(null);
  const [def, setDef] = useState<Definition | null>(null);
  // The ground, when this terrain has been surveyed. A 404 is the normal answer
  // and means the map keeps the Steam preview it had.
  const [relief, setRelief] = useState<Parameters<typeof MissionMap>[0]['relief']>(null);
  const [selected, setSelected] = useState<Selection | null>(null);
  const [panel, setPanel] = useState<PanelKey>('objectives');
  const [tool, setTool] = useState<Tool>('select');
  const [layers, setLayers] = useState<LayerState>(ALL_LAYERS);
  const [grid, setGrid] = useState(0);
  const [note, setNote] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<RevisionView[]>([]);
  /**
   * What another editor changed last, by panel (or by the title and the summary above them), and
   * who: the panel's tab wears that editor's colour until it is opened.
   */
  const [touched, setTouched] = useState<Record<string, string>>({});
  /** Who made the change the status line names, so the line wears their colour. */
  const [remoteBy, setRemoteBy] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirm, confirmDialog] = useConfirm();
  // The recipe as /api/recipes declares it, for a scripted mission: its own
  // optionals and the twists it accepts (ENGINE_DESIGN.md §2.19, §2.20).
  const [recipe, setRecipe] = useState<RecipeView | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  /** The place being moved among its kind's candidates (release D, D11.8), when «Mou» is open. */
  const [movingPlace, setMovingPlace] = useState<{ id: string; label: string } | null>(null);

  /**
   * The picture the mission is shown with. It lives on the mission, not in the
   * definition — nothing about it reaches the game — so it is its own call and
   * not a patch, and it does not move the revision the editors are working on.
   */
  async function chooseCover(choice: Cover) {
    try {
      const updated = await setMissionCover(code, choice);
      setMission((m) => (m ? { ...m, cover: updated.cover } : m));
    } catch (err) {
      setError(errorText(err));
    }
  }

  const load = useCallback(async () => {
    try {
      const m = await fetchMission(code);
      setMission(m);
      setDef(m.definition);
      setHistory(await fetchHistory(code));
      const first = m.definition.objectives[0];
      setSelected(first ? { kind: 'objective', id: first.id } : null);
    } catch (err) {
      setError(errorText(err));
    }
  }, [code]);

  useEffect(() => {
    if (me.authenticated) void load();
  }, [me.authenticated, load]);

  // The surveyed ground of this terrain, once. Most terrains have never been
  // surveyed and answer 404; the map then keeps the picture it had, and the
  // author sees no difference.
  const world = def?.terrain.worldName;
  useEffect(() => {
    if (!world) return;
    let live = true;
    void fetchHeightmap(world)
      .then((map) => {
        if (live) setRelief(map);
      })
      .catch(() => {
        if (live) setRelief(null);
      });
    return () => {
      live = false;
    };
  }, [world]);

  const scripted = !!def?.script;
  const recipeId = mission?.recipe ?? null;
  useEffect(() => {
    if (!scripted || !recipeId) return;
    let live = true;
    fetchRecipes()
      .then((list) => {
        if (live) setRecipe(list.find((r) => r.id === recipeId) ?? null);
      })
      .catch(() => {
        if (live) setRecipe(null);
      });
    return () => {
      live = false;
    };
  }, [scripted, recipeId]);

  // A panel another editor changed wears their colour until it is opened.
  useEffect(() => {
    setTouched((t) => {
      if (!(panel in t)) return t;
      const next = { ...t };
      delete next[panel];
      return next;
    });
  }, [panel]);

  // Esc leaves whatever tool is held: a designer with a tool stuck on is a designer
  // that drops a patrol every time it tries to select something.
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setTool('select');
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, []);

  // Other editors' patches arrive over the live channel and apply in place; ours we sent, so we skip them.
  const live = useDesignLive(code, !!mission?.canEdit, (m) => {
    if (m.by === me.handle) return;
    setDef((x) => {
      if (!x) return x;
      const next = structuredClone(x);
      m.ops.forEach((op) => applyOp(next, op));
      next.revision = m.revision;
      return next;
    });
    setMission((x) => (x ? { ...x, revision: m.revision } : x));
    const where: Record<string, string> = {};
    for (const op of m.ops) {
      const key = panelOfPath(op.path) ?? fieldOfPath(op.path);
      if (key) where[key] = m.by;
    }
    setTouched((t) => ({ ...t, ...where }));
    setRemoteBy(m.by);
    setStatus(ca.remoteChange(m.by));
    fetchHistory(code)
      .then(setHistory)
      .catch(() => undefined);
  });

  // Without the channel, the revision is polled: a newer one reloads the definition.
  useEffect(() => {
    if (!mission?.canEdit || live.connected) return;
    const timer = setInterval(() => {
      fetchMission(code)
        .then((m) => {
          if (m.revision > (mission?.revision ?? 0)) {
            setMission(m);
            setDef(m.definition);
            setStatus(ca.remoteChange('?'));
          }
        })
        .catch(() => undefined);
    }, 10000);
    return () => clearInterval(timer);
  }, [code, mission?.canEdit, mission?.revision, live.connected]);

  /** A field-level change lands at once as a patch against the revision we hold. */
  async function sendPatch(ops: PatchOp[]) {
    if (!mission) return;
    try {
      const m = await patchMission(code, mission.revision, ops);
      setMission(m);
      setDef((x) => (x ? { ...x, revision: m.revision } : x));
      setHistory(await fetchHistory(code));
      setStatus(`${ca.saved} · ${ca.revision(m.revision)}`);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'stale_revision') {
        await load();
        setError(ca.staleRevision);
      } else {
        setError(errorText(err));
      }
    }
  }

  const update = useCallback((patch: (d: Definition) => Definition) => {
    setDef((x) => (x ? patch(structuredClone(x)) : x));
    setStatus(null);
  }, []);

  const select = useCallback((selection: Selection | null) => {
    setSelected(selection);
    if (selection && selection.kind !== 'place') setPanel(PANEL_OF[selection.kind]);
  }, []);

  type Decision = NonNullable<Definition['decisionMatrix']>[string];
  const setDecision = (key: string, value: Decision) =>
    update((x) => ({ ...x, decisionMatrix: { ...(x.decisionMatrix ?? {}), [key]: value } }));
  const removeDecision = (key: string) =>
    update((x) => {
      const matrix = { ...(x.decisionMatrix ?? {}) };
      delete matrix[key];
      return { ...x, decisionMatrix: matrix };
    });
  const minutesOf = (closesAt?: string) => {
    const found = /^(?:T[+])?([0-9]+)m$/.exec(closesAt ?? '');
    return found ? Number(found[1]) : 3;
  };

  function updateObjective(id: string, patch: Partial<Objective>) {
    update((x) => ({
      ...x,
      objectives: x.objectives.map((o) => (o.id === id ? { ...o, ...patch } : o)),
    }));
  }

  function addObjective(type: string) {
    if (!def) return;
    const id = `${type}_${def.objectives.length + 1}`;
    const base = def.objectives[0]?.position ?? [5000, 5000];
    const objective: Objective = {
      id,
      type,
      title: type,
      position: [base[0] + 500, base[1] + 300],
      radius: 250,
      priority: 'secondary',
      // On a scripted night an added objective is secondary unless the editor says additional (D11.10).
      ...(def.script ? { role: 'secondary' as const } : {}),
      dependsOn: def.objectives[0] ? [def.objectives[0].id] : [],
      params: {},
    };
    update((x) => ({ ...x, objectives: [...x.objectives, objective] }));
    select({ kind: 'objective', id });
  }

  /** A click on empty ground: what it makes depends on the tool held. */
  function place(x: number, y: number) {
    if (!def || !mission?.canEdit || mission.editLock) return;
    // A scripted force's route and watch are the script's (D11.4).
    if ((tool === 'route' || tool === 'watch') && selected?.kind === 'force') {
      const force = (def.forces ?? []).find((f) => f.id === selected.id);
      const rule = force ? ruleFor(def, '/forces', force) : null;
      if (rule && !mayChange(rule, tool === 'route' ? 'waypoints' : 'watch')) {
        setStatus(ca.design.lockedMove(rule.reason));
        return;
      }
    }
    switch (tool) {
      case 'marker': {
        const marker = newMarker(def, x, y);
        update((current) => ({ ...current, markers: [...(current.markers ?? []), marker] }));
        select({ kind: 'marker', id: marker.id });
        break;
      }
      case 'intel': {
        const intel = newIntel(def, x, y);
        update((current) => ({ ...current, intel: [...(current.intel ?? []), intel] }));
        select({ kind: 'intel', id: intel.id });
        break;
      }
      case 'civilian': {
        const zone = newCivilianZone(def, x, y);
        update((current) => ({
          ...current,
          civilians: { enabled: true, zones: [...(current.civilians?.zones ?? []), zone] },
        }));
        select({ kind: 'civilian', id: zone.id ?? '' });
        break;
      }
      case 'route': {
        if (selected?.kind !== 'force') break;
        update((current) => ({
          ...current,
          forces: (current.forces ?? []).map((f) =>
            f.id === selected.id ? { ...f, waypoints: [...(f.waypoints ?? []), [x, y]] } : f,
          ),
        }));
        break;
      }
      case 'watch': {
        if (selected?.kind !== 'force') break;
        update((current) => ({
          ...current,
          forces: (current.forces ?? []).map((f) =>
            f.id === selected.id ? { ...f, watch: [x, y] } : f,
          ),
        }));
        setTool('select');
        break;
      }
      case 'composition': {
        const composition = newComposition(def, x, y);
        update((current) => ({
          ...current,
          compositions: [...(current.compositions ?? []), composition],
        }));
        select({ kind: 'composition', id: composition.id });
        setTool('select');
        break;
      }
      default: {
        const role = FORCE_TOOLS[tool];
        if (!role) break;
        const made = newForce(def, role, x, y);
        // On a scripted night an enemy dropped near a camp answers the camp's
        // alarm by default (D11.10; the panel's check turns it off).
        const alert =
          def.script && made.side !== 'civilian' ? defaultAlertWith(def, x, y) : undefined;
        const force = alert ? { ...made, alertWith: alert } : made;
        update((current) => ({ ...current, forces: [...(current.forces ?? []), force] }));
        select({ kind: 'force', id: force.id });
      }
    }
  }

  /**
   * Why a thing on the map may not be dragged (release D, D11.4): what the
   * script placed moves only with the script's own move; null when it may.
   */
  function lockedMove(selection: Selection): string | null {
    if (!def) return null;
    const lists: Record<string, [string, { id: string }[]]> = {
      objective: ['/objectives', def.objectives],
      force: ['/forces', def.forces ?? []],
      composition: ['/compositions', def.compositions ?? []],
    };
    const list = lists[selection.kind];
    const entry = list?.[1].find((e) => e.id === selection.id);
    if (!list || !entry) return null;
    const rule = ruleFor(def, list[0], entry as never);
    return rule && !mayChange(rule, 'position') ? rule.reason : null;
  }

  /** Something dragged on the map. Everything that has a position can be moved. */
  function move(selection: Selection, x: number, y: number) {
    if (!mission?.canEdit || mission.editLock) return;
    const why = lockedMove(selection);
    if (why) {
      setStatus(ca.design.lockedMove(why));
      return;
    }
    update((current) => {
      switch (selection.kind) {
        case 'objective':
          return {
            ...current,
            objectives: current.objectives.map((o) =>
              o.id === selection.id ? { ...o, position: [x, y] } : o,
            ),
          };
        case 'marker':
          return {
            ...current,
            markers: (current.markers ?? []).map((m) =>
              m.id === selection.id ? { ...m, position: [x, y] } : m,
            ),
          };
        case 'force':
          return {
            ...current,
            forces: (current.forces ?? []).map((f) =>
              f.id === selection.id ? { ...f, position: [x, y] } : f,
            ),
          };
        case 'composition':
          return {
            ...current,
            compositions: (current.compositions ?? []).map((c) =>
              c.id === selection.id ? { ...c, position: [x, y] } : c,
            ),
          };
        case 'intel':
          return {
            ...current,
            intel: (current.intel ?? []).map((n) =>
              n.id === selection.id ? { ...n, position: [x, y] } : n,
            ),
          };
        case 'civilian':
          return {
            ...current,
            civilians: {
              ...(current.civilians ?? {}),
              zones: (current.civilians?.zones ?? []).map((z) =>
                z.id === selection.id ? { ...z, position: [x, y] } : z,
              ),
            },
          };
        default:
          return current;
      }
    });
  }

  /** A box drawn corner to corner: the area of operations, or a zone inside it. */
  function drawBox(cx: number, cy: number, a: number, b: number) {
    if (tool === 'area') {
      update((current) => ({
        ...current,
        area: { ...(current.area ?? newArea(cx, cy)), shape: 'rect', center: [cx, cy], a, b },
      }));
      select({ kind: 'area', id: 'area' });
      setTool('select');
      return;
    }
    if (tool === 'zone' && def) {
      const zone = { ...newRestrictedZone(def, cx, cy), a, b };
      update((current) => ({
        ...current,
        area: {
          ...(current.area ?? newArea(cx, cy, a * 2, b * 2)),
          restricted: [...(current.area?.restricted ?? []), zone],
        },
      }));
      select({ kind: 'zone', id: zone.id ?? '' });
      setTool('select');
    }
  }

  /** The same content, whatever the revision field says: the platform sets that one. */
  const sameDefinition = (a: Definition, b: Definition | undefined) =>
    !!b && JSON.stringify({ ...a, revision: 0 }) === JSON.stringify({ ...b, revision: 0 });

  /**
   * A recipe's own option or an accepted twist turned on or off: the mission is
   * generated again from its own request with that one choice changed, on the
   * same code and seed, as the next revision — never a hand edit of the script
   * (ENGINE_DESIGN.md §2.20). What was edited by hand since is replaced, so it
   * asks first.
   */
  async function toggleOptional(optional: OptionalView) {
    if (!def || !mission) return;
    const accepted = recipe?.optionals ?? [];
    const change =
      optional.kind === 'twist'
        ? { twists: twistsWith(accepted, def, optional.key) }
        : { secondaryObjectives: secondariesWith(accepted, def, optional.key) };
    await regenerateWith(change);
  }

  /**
   * One of the type's own settings changed (release D, D6.3): generated again
   * the same way, with only that setting sent, merged over the request's own.
   */
  async function changeSetting(id: string, value: string) {
    if (!def || !mission) return;
    await regenerateWith({ settings: { [id]: value } });
  }

  async function regenerateWith(change: Parameters<typeof regenerateMission>[2]) {
    if (!def || !mission) return;
    const ok = await confirm({
      ...ca.script.regenerateAsk,
      confirmLabel: ca.script.regenerateConfirm,
    });
    if (!ok) return;
    setRegenerating(true);
    try {
      const m = await regenerateMission(code, mission.revision, change);
      setMission(m);
      setDef(m.definition);
      setHistory(await fetchHistory(code));
      setStatus(`${ca.script.regenerated} · ${ca.revision(m.revision)}`);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'stale_revision') {
        await load();
        setError(ca.staleRevision);
      } else {
        setError(messageOf(err));
      }
    } finally {
      setRegenerating(false);
    }
  }

  async function save() {
    if (!def) return;
    // Every field-level patch already landed as a revision when its input lost
    // focus; a "Desa" over a definition the server already holds would write a
    // second, identical revision (one edit made two until 2026-09-08).
    if (sameDefinition(def, mission?.definition)) {
      setStatus(ca.nothingToSave(mission?.revision ?? def.revision));
      return;
    }
    try {
      const m = await saveMission(
        code,
        def,
        note || 'edited in the designer',
        mission?.revision ?? def.revision,
      );
      setMission(m);
      setDef(m.definition);
      setHistory(await fetchHistory(code));
      setNote('');
      setStatus(`${ca.saved} · ${ca.revision(m.revision)}`);
      setError(null);
    } catch (err) {
      // Somebody else wrote since this copy was loaded: a whole-document save
      // would undo their change, so the designer reloads and says so.
      if (err instanceof ApiError && err.code === 'stale_revision') {
        await load();
        setError(ca.staleRevision);
      } else {
        setError(errorText(err));
      }
    }
  }

  if (!mission || !def) {
    // Until the mission answers the page said nothing at all, which reads as
    // "there is nothing here" rather than "it is coming".
    return (
      <Shell title={ca.designer} width="wide">
        {error ? (
          <p className="error" role="alert">
            {error}
          </p>
        ) : (
          <PageLoading />
        )}
      </Shell>
    );
  }

  const current =
    selected?.kind === 'objective'
      ? (def.objectives.find((o) => o.id === selected.id) ?? null)
      : null;
  const problems = checks(def);
  // A smoke night's, an inspection's or a played chapter's mission opens read-only (D11.4).
  const readOnly = !mission.canEdit || !!mission.editLock;
  // On the map a place is its kind and its name; the Guió panel keeps the generator's own line.
  const places = placesOf(def, placeLabel);
  const selectedPlace =
    selected?.kind === 'place' ? places.find((p) => p.id === selected.id) : undefined;
  const placeName = (id: string) =>
    ca.script.pointNames[id] ?? places.find((p) => p.id === id)?.label ?? id;
  const openMove = readOnly
    ? undefined
    : (id: string, label: string) => setMovingPlace({ id, label });
  async function placeMoved(m: MissionView) {
    setMission(m);
    setDef(m.definition);
    setMovingPlace(null);
    setHistory(await fetchHistory(code));
    setStatus(`${ca.script.moved} · ${ca.revision(m.revision)}`);
    setError(null);
  }
  async function placeMoveFailed(err: unknown) {
    setMovingPlace(null);
    if (err instanceof ApiError && err.code === 'stale_revision') {
      await load();
      setError(ca.staleRevision);
    } else {
      setError(messageOf(err));
    }
  }
  const objectiveRule = current ? ruleFor(def, '/objectives', current) : null;
  const addedOnScript = !!def.script && !!current && !objectiveRule;
  const errors = problems.filter((c) => c.level === 'error').length;
  // The other editors in the room, one each however many tabs they have open, and every
  // editor's colour, worked out from the same names in every browser (editorColours.ts).
  const others = live.editors
    .filter((e) => e.handle !== me.handle)
    .filter((e, i, all) => all.findIndex((o) => o.handle === e.handle) === i);
  const slots = editorSlots([
    me.handle,
    ...live.editors.map((e) => e.handle),
    ...history.map((h) => h.authorHandle),
  ]);
  const wears = (handle: string | null | undefined): CSSProperties =>
    ({ '--editor': editorColour(handle ? slots.get(handle) : undefined) }) as CSSProperties;
  const nameOf = (handle: string) => {
    const e = live.editors.find((x) => x.handle === handle);
    return e?.displayName || handle;
  };
  const objectiveIds = def.objectives.map((o) => o.id);
  const panelOf = (focus: string) => panelOfFocus(focus, PANELS, objectiveIds) as PanelKey | null;
  const whereIs = (focus: string): string | null => {
    const p = panelOf(focus);
    if (p) return ca.design.onPanel(ca.design.panels[p]);
    if (focus === 'title') return ca.design.fieldTitle;
    if (focus === 'summary') return ca.design.fieldSummary;
    return null;
  };
  /** Who else is on a field above the panels. */
  const onField = (field: 'title' | 'summary') => others.filter((e) => e.panel === field);
  /** The colour a field above the panels wears: whoever is on it, else whoever changed it last. */
  const fieldStyle = (field: 'title' | 'summary'): CSSProperties | undefined => {
    const by = onField(field)[0]?.handle ?? touched[field];
    return by ? wears(by) : undefined;
  };
  const fieldMark = (field: 'title' | 'summary') => {
    const here = onField(field);
    const by = touched[field];
    if (here.length === 0 && !by) return null;
    return (
      <span className="editor-marks">
        {here.map((e) => (
          <span key={e.handle} className="editor-chip" style={wears(e.handle)}>
            <i className="bi bi-pencil" aria-hidden="true" />{' '}
            {ca.design.editorTyping(e.displayName || e.handle)}
          </span>
        ))}
        {here.length === 0 && by && (
          <span className="editor-chip" style={wears(by)}>
            {ca.design.editorChanged(nameOf(by))}
          </span>
        )}
      </span>
    );
  };
  const seenField = (field: 'title' | 'summary') =>
    setTouched((t) => {
      if (!(field in t)) return t;
      const next = { ...t };
      delete next[field];
      return next;
    });
  const panelProps = { def, update, selected, select, setTool };
  /**
   * A support the script offers only from a phase (ENGINE_DESIGN.md §2.11:
   * `supports.gated.<key>` names the phase), in words: the recipe's own
   * sentence when it declares one, else the phase's title.
   */
  const gated = (def.supports as Record<string, unknown> | undefined)?.gated as
    Record<string, string> | undefined;
  const gateOf = (key: string): string | null => {
    const at = gated?.[key];
    if (!at) return null;
    if (recipe?.gates?.[key]) return recipe.gates[key];
    const phase = at.replace(/^phase:/, '');
    return def.script?.phases?.find((p) => p.id === phase)?.title ?? phase;
  };

  async function removeMission() {
    const ask = ca.deleteMissionConfirm(def!.title);
    if (!(await confirm({ ...ask, confirmLabel: ca.deleteMission, danger: true }))) return;
    try {
      await deleteMission(code);
      navigate('/library');
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <Shell
      title={ca.designer}
      width="wide"
      chips={
        <>
          <span className="chip">{ca.revision(mission.revision)}</span>
          {errors > 0 && <span className="chip danger">{ca.design.checkErrors(errors)}</span>}
          <span className="presence" role="group" aria-label={ca.presence}>
            {!live.connected ? (
              <span className="muted small">{ca.liveOff}</span>
            ) : others.length === 0 ? (
              <span className="muted small">{ca.presenceAlone}</span>
            ) : (
              others.map((e) => {
                const where = whereIs(e.panel);
                const name = e.displayName || e.handle;
                return (
                  <span key={e.handle} className="editor-chip" style={wears(e.handle)}>
                    <i className="bi bi-person-fill" aria-hidden="true" />{' '}
                    {where ? ca.design.editorOn(name, where) : name}
                  </span>
                );
              })
            )}
          </span>
          {status && (
            <span className="muted small status-line" role="status">
              {remoteBy && status === ca.remoteChange(remoteBy) && (
                <span className="editor-dot" style={wears(remoteBy)} aria-hidden="true" />
              )}
              {status}
            </span>
          )}
        </>
      }
      actions={
        <>
          <PlannedButton id="designer-generate-whole">{ca.design.generateWhole}</PlannedButton>
          <PlannedButton id="designer-preview">{ca.design.preview}</PlannedButton>
          <button type="button" onClick={() => setShowHistory((s) => !s)}>
            {showHistory ? ca.design.closeHistory : ca.design.historyButton}
          </button>
          <input
            name="note"
            placeholder={ca.saveNote}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <button type="button" className="primary" disabled={readOnly} onClick={() => void save()}>
            {ca.save}
          </button>
        </>
      }
    >
      {confirmDialog}
      {mission.editLock && (
        <p className="notice warn" role="note" aria-label={ca.design.readOnlyTitle}>
          <i className="bi bi-lock" aria-hidden="true" />{' '}
          <strong>{ca.design.readOnlyTitle}.</strong> {mission.editLock.reason}
        </p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <div className="split">
        <nav className="panel-rail" role="tablist" aria-label={ca.design.panelRail}>
          {PANELS.filter((p) => p !== 'script' || !!def.script).map((p) => (
            <button
              key={p}
              type="button"
              role="tab"
              aria-selected={panel === p}
              className={panel === p ? 'active' : ''}
              onClick={() => {
                setPanel(p);
                live.focus(p);
              }}
            >
              {ca.design.panels[p]}
              {(touched[p] || others.some((e) => panelOf(e.panel) === p)) && (
                <span className="editor-marks">
                  {others
                    .filter((e) => panelOf(e.panel) === p)
                    .map((e) => (
                      <span
                        key={e.handle}
                        className="editor-dot"
                        style={wears(e.handle)}
                        role="img"
                        aria-label={ca.design.editorHere(e.displayName || e.handle)}
                        title={ca.design.editorHere(e.displayName || e.handle)}
                      />
                    ))}
                  {touched[p] && (
                    <i
                      className="bi bi-pencil-fill editor-touched"
                      style={wears(touched[p])}
                      role="img"
                      aria-label={ca.design.editorChanged(nameOf(touched[p]))}
                      title={ca.design.editorChanged(nameOf(touched[p]))}
                    />
                  )}
                </span>
              )}
            </button>
          ))}
        </nav>
        <section className="cards">
          <article className="card map-card">
            <MapChrome
              layers={layers}
              setLayers={setLayers}
              tool={tool}
              setTool={setTool}
              grid={grid}
              setGrid={setGrid}
            />
            <MissionMap
              relief={relief}
              world={def.terrain.worldName}
              worldSize={worldSizeOf(def)}
              objectives={def.objectives}
              start={def.sides.players.startPosition}
              area={def.area}
              markers={def.markers}
              forces={def.forces}
              compositions={def.compositions}
              intel={def.intel}
              civilians={def.civilians?.zones}
              places={places}
              layers={layers}
              selected={selected}
              tool={tool}
              grid={grid}
              onSelect={select}
              onPlace={place}
              onMove={move}
              onDrawBox={drawBox}
              height={620}
              // As wide as its column, and open on the area of operations (the middle of the
              // terrain when the mission has none).
              wide
              fit="area"
            />
            <div className="row">
              <select
                name="addType"
                disabled={readOnly}
                aria-label={ca.addObjective}
                onChange={(e) => e.target.value && addObjective(e.target.value)}
                value=""
              >
                <option value="">{ca.addObjective}…</option>
                {TYPES.map((t) => (
                  <option key={t} value={t}>
                    {ca.objectiveUi.types[t] ?? t}
                  </option>
                ))}
              </select>
              <span className="muted small">{ca.design.dragToMove}</span>
            </div>
            {selectedPlace && (
              <div className="row" role="group" aria-label={selectedPlace.label}>
                <span className="small grow">
                  {ca.script.pointNames[selectedPlace.id] ?? selectedPlace.label}
                  {!selectedPlace.movable && (
                    <span className="muted block">{ca.script.alternative}</span>
                  )}
                </span>
                {openMove && selectedPlace.movable && (
                  <button
                    type="button"
                    className="small"
                    disabled={regenerating}
                    onClick={() => openMove(selectedPlace.id, placeName(selectedPlace.id))}
                  >
                    {ca.script.move}
                  </button>
                )}
              </div>
            )}
          </article>
          {movingPlace && (
            <PlaceMover
              code={code}
              revision={mission.revision}
              place={movingPlace}
              world={def.terrain.worldName}
              worldSize={worldSizeOf(def)}
              names={placeName}
              onMoved={(m) => void placeMoved(m)}
              onFailed={(err) => void placeMoveFailed(err)}
              onCancel={() => setMovingPlace(null)}
            />
          )}
          <article className="card form">
            <fieldset className="bare" disabled={readOnly}>
              <label>
                <span className="row">
                  {ca.objectiveTitle} ({ca.mission}) {fieldMark('title')}
                </span>
                <input
                  name="title"
                  maxLength={80}
                  value={def.title}
                  className={fieldStyle('title') ? 'edited-by' : undefined}
                  style={fieldStyle('title')}
                  onFocus={() => {
                    live.focus('title');
                    seenField('title');
                  }}
                  onChange={(e) => update((x) => ({ ...x, title: e.target.value }))}
                  onBlur={(e) =>
                    e.target.value !== mission.definition.title &&
                    void sendPatch([{ path: '/title', value: e.target.value }])
                  }
                />
              </label>
              <label>
                <span className="row">
                  {ca.summary} {fieldMark('summary')}
                </span>
                <textarea
                  name="summary"
                  rows={4}
                  value={def.summary ?? ''}
                  className={fieldStyle('summary') ? 'edited-by' : undefined}
                  style={fieldStyle('summary')}
                  onFocus={() => {
                    live.focus('summary');
                    seenField('summary');
                  }}
                  onChange={(e) => update((x) => ({ ...x, summary: e.target.value }))}
                  onBlur={(e) =>
                    e.target.value !== (mission.definition.summary ?? '') &&
                    void sendPatch([{ path: '/summary', value: e.target.value }])
                  }
                />
              </label>
            </fieldset>
          </article>
        </section>
        <aside className="side">
          <fieldset className="bare" disabled={readOnly}>
            {panel === 'setup' && (
              <>
                <SetupPanel
                  def={def}
                  update={update}
                  fixed={recipe?.fixed}
                  constraints={recipe?.constraints}
                  recipeSupports={recipe?.supports}
                  busy={regenerating}
                  onParams={(params) => void regenerateWith({ params })}
                />
                {mission.canEdit && (
                  <CoverPanel mission={mission} onPick={(choice) => void chooseCover(choice)} />
                )}
                <section className="card" aria-label={ca.missionUi.page.downloadZip}>
                  <h2>{ca.design.downloadTitle}</h2>
                  <p className="muted small">{ca.missionUi.page.downloadHint}</p>
                  <div className="row">
                    <a className="button" href={missionDownloadUrl(code, 'zip')}>
                      <i className="bi bi-file-earmark-zip" aria-hidden="true" />
                      {ca.missionUi.page.downloadZip}
                    </a>
                    <a className="button ghost" href={missionDownloadUrl(code, 'pbo')}>
                      <i className="bi bi-box-seam" aria-hidden="true" />
                      {ca.missionUi.page.downloadPbo}
                    </a>
                  </div>
                </section>
                {mission.canEdit && (
                  <section className="card">
                    <div className="danger-zone">
                      <p className="muted small">{ca.design.deleteHint}</p>
                      <button type="button" className="danger" onClick={() => void removeMission()}>
                        {ca.deleteMission}
                      </button>
                    </div>
                  </section>
                )}
              </>
            )}
            {panel === 'objectives' && current && (
              <section className="card form" aria-label={current.id}>
                <h2>
                  {current.title || current.id}
                  {current.priority === 'hidden' && (
                    <span className="chip" title={ca.hiddenObjective}>
                      {ca.priorityNames.hidden}
                    </span>
                  )}
                </h2>
                <label>
                  {ca.objectiveTitle}
                  <input
                    name="objTitle"
                    value={current.title ?? ''}
                    onFocus={() => live.focus(current.id)}
                    onChange={(e) => updateObjective(current.id, { title: e.target.value })}
                    onBlur={(e) => {
                      const index = def.objectives.findIndex((o) => o.id === current.id);
                      if (
                        index >= 0 &&
                        e.target.value !== mission.definition.objectives[index]?.title
                      )
                        void sendPatch([
                          { path: `/objectives/${index}/title`, value: e.target.value },
                        ]);
                    }}
                  />
                </label>
                <div className="two">
                  <label>
                    {ca.objectiveType}
                    <select
                      name="objType"
                      disabled={!!objectiveRule}
                      value={current.type}
                      onChange={(e) => updateObjective(current.id, { type: e.target.value })}
                    >
                      {/* An objective of a type the list does not offer (a recipe's own) keeps its option. */}
                      {(TYPES.includes(current.type) ? TYPES : [current.type, ...TYPES]).map(
                        (t) => (
                          <option key={t} value={t}>
                            {ca.objectiveUi.types[t] ?? t}
                          </option>
                        ),
                      )}
                    </select>
                  </label>
                  <label>
                    {ca.objectivePriority}
                    <select
                      name="objPriority"
                      disabled={!!objectiveRule}
                      value={current.priority ?? 'main'}
                      onChange={(e) =>
                        updateObjective(current.id, {
                          priority: e.target.value as Objective['priority'],
                        })
                      }
                    >
                      {Object.entries(ca.priorityNames)
                        // On a scripted night the script's end rules own the main line.
                        .filter(([k]) => !addedOnScript || k !== 'main')
                        .map(([k, v]) => (
                          <option key={k} value={k}>
                            {v}
                          </option>
                        ))}
                    </select>
                  </label>
                </div>
                {addedOnScript && (
                  <label>
                    {ca.design.objectiveRole}
                    <select
                      name="objRole"
                      value={current.role === 'additional' ? 'additional' : 'secondary'}
                      onChange={(e) =>
                        updateObjective(current.id, {
                          role: e.target.value as Objective['role'],
                        })
                      }
                    >
                      {Object.entries(ca.design.objectiveRoles).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label>
                  {ca.objectiveRadius}
                  <input
                    name="objRadius"
                    disabled={!!objectiveRule}
                    type="number"
                    min={50}
                    max={2000}
                    value={current.radius ?? 250}
                    onChange={(e) =>
                      updateObjective(current.id, { radius: Number(e.target.value) })
                    }
                  />
                </label>
                <p className="muted small">
                  {Math.round(current.position[0])}, {Math.round(current.position[1])}
                  {current.dependsOn?.length ? ` · ← ${current.dependsOn.join(', ')}` : ''}
                </p>
                {objectiveRule && (
                  <p className="muted small lock-note">
                    <i className="bi bi-lock" aria-hidden="true" /> {objectiveRule.reason}
                  </p>
                )}
                {!objectiveRule && (
                  <button
                    type="button"
                    className="danger"
                    disabled={def.objectives.length <= 1}
                    onClick={() => {
                      update((x) => ({
                        ...x,
                        objectives: x.objectives.filter((o) => o.id !== current.id),
                      }));
                      setSelected(null);
                    }}
                  >
                    {ca.removeObjective}
                  </button>
                )}
              </section>
            )}
            {panel === 'area' && <AreaPanel {...panelProps} />}
            {panel === 'markers' && <MarkersPanel {...panelProps} />}
            {panel === 'forces' && <ForcesPanel {...panelProps} />}
            {panel === 'compositions' && <CompositionsPanel {...panelProps} />}
            {panel === 'civilians' && <CiviliansPanel {...panelProps} />}
            {panel === 'intel' && <IntelPanel {...panelProps} />}
            {panel === 'script' && (
              <ScriptPanel
                def={def}
                recipe={recipe}
                busy={regenerating}
                onToggle={(o) => void toggleOptional(o)}
                onSetting={(id, value) => void changeSetting(id, value)}
                onMove={openMove}
              />
            )}
            {panel === 'twists' && (
              <TwistsPanel
                {...panelProps}
                accepted={
                  def.script
                    ? (recipe?.optionals ?? []).filter((o) => o.kind === 'twist')
                    : undefined
                }
                busy={regenerating}
                onToggle={(o) => void toggleOptional(o)}
              />
            )}
            {panel === 'briefing' && <BriefingPanel def={def} update={update} />}
            {panel === 'cast' && <CastPanel def={def} update={update} code={code} />}
            {panel === 'story' && <StoryPanel def={def} update={update} code={code} />}
            {panel === 'factions' && (
              <FactionsPanel
                def={def}
                busy={regenerating}
                onChange={(params) => void regenerateWith({ params })}
              />
            )}
            {panel === 'loadouts' && (
              <section className="card" aria-label={ca.design.panels.loadouts}>
                <h2>{ca.design.panels.loadouts}</h2>
                <p className="muted small">{ca.design.loadoutsHint}</p>
                <Link to="/catalog" className="button">
                  <i className="bi bi-backpack" aria-hidden="true" /> {ca.design.loadoutsLink}
                </Link>
              </section>
            )}
            {panel === 'supports' && (
              <section className="card form" aria-label={ca.supportsWhich}>
                <h2>{ca.supportsWhich}</h2>
                <p className="muted small">{ca.supportsNoCountHint}</p>
                <div className="two">
                  {Object.entries(ca.supportNames).map(([k, name]) => (
                    <label key={k} className="check">
                      <input
                        name={`support-${k}`}
                        type="checkbox"
                        checked={(def.supports?.[k] ?? 0) > 0}
                        onChange={(e) =>
                          update((x) => ({
                            ...x,
                            supports: { ...(x.supports ?? {}), [k]: e.target.checked ? 1 : 0 },
                          }))
                        }
                      />
                      <span>
                        {name}
                        {gateOf(k) && (
                          <span className="muted small block">
                            {ca.script.gate(gateOf(k) ?? '')}
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </section>
            )}
            {panel === 'rules' && (
              <>
                <section className="card form" aria-label={ca.whoDecides}>
                  <h2>{ca.whoDecides}</h2>
                  <p className="muted small">{ca.decisionsHint}</p>
                  {Object.entries(def.decisionMatrix ?? {}).map(([key, value]) => (
                    <div className="stack" key={key}>
                      <strong>{ca.decisionKeys[key] ?? key}</strong>
                      <div className="two">
                        <label>
                          {ca.decidedByLabel}
                          <select
                            value={value.by}
                            onChange={(e) => setDecision(key, { ...value, by: e.target.value })}
                          >
                            {Object.entries(ca.decidedBy).map(([v, name]) => (
                              <option key={v} value={v}>
                                {name}
                              </option>
                            ))}
                          </select>
                        </label>
                        {value.by === 'voteDuring' && (
                          <label>
                            {ca.opensAtObjective}
                            <select
                              value={value.opensAt ?? 'start'}
                              onChange={(e) =>
                                setDecision(key, { ...value, opensAt: e.target.value })
                              }
                            >
                              <option value="start">{ca.atStart}</option>
                              {def.objectives.map((o) => (
                                <option key={o.id} value={`onObjective:${o.id}`}>
                                  {o.title ?? o.id}
                                </option>
                              ))}
                            </select>
                          </label>
                        )}
                        {value.by === 'voteDuring' && (
                          <label>
                            {ca.closesAfterMinutes}
                            <input
                              type="number"
                              min={1}
                              max={30}
                              value={minutesOf(value.closesAt)}
                              onChange={(e) =>
                                setDecision(key, { ...value, closesAt: `${e.target.value}m` })
                              }
                            />
                          </label>
                        )}
                      </div>
                      {(value.by === 'voteBefore' || value.by === 'voteDuring') && (
                        <label>
                          {ca.optionsCommaSeparated}
                          <input
                            value={(value.options ?? []).join(', ')}
                            onChange={(e) =>
                              setDecision(key, {
                                ...value,
                                options: e.target.value.split(',').map((s) => s.trim()),
                              })
                            }
                          />
                        </label>
                      )}
                      <button type="button" className="danger" onClick={() => removeDecision(key)}>
                        {ca.removeDecision}
                      </button>
                    </div>
                  ))}
                  <label>
                    {ca.addDecision}
                    <select
                      value=""
                      onChange={(e) => {
                        if (e.target.value)
                          setDecision(e.target.value, {
                            by: 'voteBefore',
                            opensAt: 'publish',
                            closesAt: 'briefing',
                            options: [],
                          });
                      }}
                    >
                      <option value="">{ca.loadoutImportNone}</option>
                      {Object.entries(ca.decisionKeys)
                        .filter(([k]) => !(def.decisionMatrix ?? {})[k])
                        .map(([k, name]) => (
                          <option key={k} value={k}>
                            {name}
                          </option>
                        ))}
                    </select>
                  </label>
                </section>
                <RespawnPanel def={def} update={update} />
                <section className="card" aria-label={ca.design.panels.rules}>
                  <p className="muted small">{ca.design.rulesHint}</p>
                  <ViewDistance def={def} update={update} />
                </section>
              </>
            )}
            {panel === 'check' && (
              <CheckPanel
                def={def}
                select={select}
                setPanel={(p) => setPanel(p as PanelKey)}
                stored={mission.definitionCheck}
                preflight={mission.preflight}
              />
            )}
          </fieldset>
        </aside>
        {showHistory && (
          <aside className="drawer" aria-label={ca.history}>
            <h2>{ca.history}</h2>
            <ul className="plain">
              {history.map((h) => (
                <li key={h.revision} className="row">
                  <strong>{ca.revision(h.revision)}</strong>{' '}
                  {h.authorHandle && (
                    <span className="editor-chip" style={wears(h.authorHandle)}>
                      {ca.design.historyBy(nameOf(h.authorHandle))}
                    </span>
                  )}{' '}
                  <span className="muted small">{h.note}</span>
                  {mission && h.revision !== mission.revision && (
                    <button
                      type="button"
                      className="small"
                      onClick={() =>
                        void restoreRevision(code, h.revision)
                          .then(() => load())
                          .catch((err) => setError(errorText(err)))
                      }
                    >
                      {ca.design.restore}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </aside>
        )}
      </div>
    </Shell>
  );
}
